/**
 * API client configuration for BOSI application.
 * Uses axios for HTTP requests with automatic token refresh.
 */
import axios from 'axios';
import { getAccessToken, getRefreshToken, clearTokens } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

/**
 * Axios instance with default configuration
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Required for sending cookies and CORS with credentials
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor for adding Authorization header with access token
 */
apiClient.interceptors.request.use(
  (config) => {
    // Add Authorization header with access token
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Add any request modifications here (e.g., logging in debug mode)
    if (import.meta.env.VITE_DEBUG_MODE === 'true') {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for handling errors and token refresh
 */
apiClient.interceptors.response.use(
  (response) => {
    // Return successful responses as-is
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If 401 Unauthorized and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Attempt to refresh the access token
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );

        const { access_token } = response.data;

        if (access_token) {
          // Update access token in localStorage
          localStorage.setItem('access_token', access_token);

          // Update Authorization header in the original request
          originalRequest.headers.Authorization = `Bearer ${access_token}`;

          // Retry the original request
          return apiClient(originalRequest);
        }

        throw new Error('No access token in refresh response');
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to auth page
        clearTokens();
        window.location.href = '/auth';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

/**
 * Simple API service wrapper for convenience
 */
export const apiService = {
  async get(url: string, config?: any) {
    const response = await apiClient.get(url, config);
    return response.data;
  },
  async post(url: string, data?: any, config?: any) {
    const response = await apiClient.post(url, data, config);
    return response.data;
  },
  async patch(url: string, data?: any, config?: any) {
    const response = await apiClient.patch(url, data, config);
    return response.data;
  },
  async delete(url: string, config?: any) {
    const response = await apiClient.delete(url, config);
    return response.data;
  },
};

/**
 * React Query hooks for check-ins
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckInInputs, FeedbackResponse } from '../types';

/**
 * Hook to create a new check-in
 */
export const useCreateCheckin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inputs: CheckInInputs) => {
      const response = await apiClient.post('/checkins', { inputs });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate today's check-in query to refetch
      queryClient.invalidateQueries({ queryKey: ['checkin', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['checkins'] });
    }
  });
};

/**
 * Hook to update an existing check-in (same-day only)
 */
export const useUpdateCheckin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ checkinId, inputs }: { checkinId: string; inputs: CheckInInputs }) => {
      const response = await apiClient.patch(`/checkins/${checkinId}`, { inputs });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate today's check-in query to refetch with updated data
      queryClient.invalidateQueries({ queryKey: ['checkin', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['checkins'] });
    },
    onError: (error: any) => {
      // Log error for debugging
      if (import.meta.env.VITE_DEBUG_MODE === 'true') {
        console.error('[Update Check-in Error]', error.response?.data || error.message);
      }
    }
  });
};

/**
 * Hook to submit guidance feedback for a check-in
 */
export const useSubmitGuidanceFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ checkinId, response }: { checkinId: string; response: FeedbackResponse }) => {
      const res = await apiClient.patch(`/checkins/${checkinId}/feedback`, { response });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checkin', 'today'] });
    }
  });
};

/**
 * Hook to get today's check-in
 */
export const useGetTodayCheckin = () => {
  return useQuery({
    queryKey: ['checkin', 'today'],
    queryFn: async () => {
      const response = await apiClient.get('/checkins/today');
      // Return full response to include hrv_trend, sleep_averages, etc.
      return response.data;
    },
    retry: false, // Don't retry on 404 (no check-in today)
    refetchOnWindowFocus: false
  });
};

/**
 * Hook to get timeline data
 */
export const useTimeline = (days: number = 30) => {
  return useQuery({
    queryKey: ['progress', 'timeline', days],
    queryFn: async () => {
      const response = await apiClient.get(`/progress/timeline?days=${days}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
};

/**
 * Hook to get trend data
 */
export const useTrends = (days: number = 30) => {
  return useQuery({
    queryKey: ['progress', 'trends', days],
    queryFn: async () => {
      const response = await apiClient.get(`/progress/trends?days=${days}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000
  });
};

/**
 * Hook to get pattern insights
 */
export const usePatterns = () => {
  return useQuery({
    queryKey: ['progress', 'patterns'],
    queryFn: async () => {
      const response = await apiClient.get('/progress/patterns');
      return response.data;
    },
    staleTime: 10 * 60 * 1000 // 10 minutes
  });
};

/**
 * Hook to get HRV trend data
 */
export const useHRVTrends = (days: number = 30) => {
  return useQuery({
    queryKey: ['progress', 'hrv-trends', days],
    queryFn: async () => {
      const response = await apiClient.get(`/progress/hrv-trends?days=${days}`);
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1 // Retry once on failure
  });
};

/**
 * Hook to get sleep stage trend data
 */
export const useSleepStageTrends = (days: number = 30) => {
  return useQuery({
    queryKey: ['progress', 'sleep-stage-trends', days],
    queryFn: async () => {
      const response = await apiClient.get(`/progress/sleep-stage-trends?days=${days}`);
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1 // Retry once on failure
  });
};

/**
 * Hook to get correlation insights
 */
export const useCorrelations = (days: number = 30) => {
  return useQuery({
    queryKey: ['progress', 'correlations', days],
    queryFn: async () => {
      const response = await apiClient.get(`/progress/correlations?days=${days}`);
      return response.data;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes (correlations change slowly)
    retry: 1,
    enabled: days >= 30 // Only fetch if we have enough data
  });
};

/**
 * Hook to get optimal ranges
 */
export const useOptimalRanges = (days: number = 30, targetMetric: string = 'energy_level') => {
  return useQuery({
    queryKey: ['progress', 'optimal-ranges', days, targetMetric],
    queryFn: async () => {
      const response = await apiClient.get(`/progress/optimal-ranges?days=${days}&target_metric=${targetMetric}`);
      return response.data;
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    retry: 1,
    enabled: days >= 30 // Only fetch if we have enough data
  });
};

/**
 * Hook to export progress data as PDF
 */
export const useExportPDF = () => {
  return useMutation({
    mutationFn: async (params: {
      dateRange: { start_date: string; end_date: string };
      sections?: {
        include_summary?: boolean;
        include_hrv_trends?: boolean;
        include_sleep_stage_trends?: boolean;
        include_correlations?: boolean;
        include_pattern_insights?: boolean;
      };
      userInfo?: {
        name?: string;
        date_of_birth?: string;
        provider_name?: string;
      };
    }) => {
      const response = await apiClient.post('/progress/export/pdf', params, {
        responseType: 'blob'
      });
      return response.data;
    }
  });
};

/**
 * Hook to export progress data as CSV
 */
export const useExportCSV = () => {
  return useMutation({
    mutationFn: async (params: {
      start_date: string;
      end_date: string;
      include_calculated_metrics?: boolean;
    }) => {
      const response = await apiClient.get('/progress/export/csv', {
        params,
        responseType: 'blob'
      });
      return response.data;
    }
  });
};

// ============================================================================
// Blood Pressure API Functions
// ============================================================================

import type {
  BPReadingInput,
  BPReading,
  BPReadingsListResponse,
  BPTrendsResponse,
  BPReadingsQueryParams
} from '../types/bloodPressure';

/**
 * Create new BP reading
 */
export const createBPReading = async (data: BPReadingInput): Promise<BPReading> => {
  const response = await apiClient.post('/bp/readings', data);
  return response.data;
};

/**
 * Get user's BP readings with optional filters
 */
export const getBPReadings = async (params?: BPReadingsQueryParams): Promise<BPReadingsListResponse> => {
  const response = await apiClient.get('/bp/readings', { params });
  return response.data;
};

/**
 * Get single BP reading by ID
 */
export const getBPReadingById = async (readingId: string): Promise<BPReading> => {
  const response = await apiClient.get(`/bp/readings/${readingId}`);
  return response.data;
};

/**
 * Update BP reading (partial update)
 */
export const updateBPReading = async (
  readingId: string,
  data: Partial<BPReadingInput>
): Promise<BPReading> => {
  const response = await apiClient.patch(`/bp/readings/${readingId}`, data);
  return response.data;
};

/**
 * Delete BP reading
 */
export const deleteBPReading = async (readingId: string): Promise<{ message: string; deleted_id: string }> => {
  const response = await apiClient.delete(`/bp/readings/${readingId}`);
  return response.data;
};

/**
 * Get BP trend data for charts
 */
export const getBPTrends = async (days: number = 30): Promise<BPTrendsResponse> => {
  const response = await apiClient.get('/bp/trends', { params: { days } });
  return response.data;
};

/**
 * Get today's BP readings
 */
export const getTodayBPReadings = async (): Promise<BPReading[]> => {
  const response = await getBPReadings({ days: 1 });
  return response.readings;
};

/**
 * Update user's onboarding status
 * Feature: 011-registration-welcome
 *
 * @param action - 'completed', 'dismissed', or 'viewed'
 * @returns Updated user object
 */
export const updateOnboardingStatus = async (action: 'completed' | 'dismissed' | 'viewed'): Promise<any> => {
  const response = await apiClient.patch('/profile/onboarding', { action });
  return response.data;
};

/**
 * Save onboarding page data and update progress
 * Feature: 016-onboarding-conversation
 *
 * @param page - Page identifier: personal_details, goals, body_state, or struggles
 * @param data - Profile fields to update for this page
 * @returns Updated user object with new onboarding progress
 */
export const saveOnboardingPage = async (
  page: 'personal_details' | 'goals' | 'body_state' | 'struggles',
  data: any
): Promise<any> => {
  const response = await apiClient.patch('/onboarding/page', { page, data });
  return response.data;
};
